// Conciliacion de los pagos en linea contra el reporte "Transacciones Payworks 2" de Banorte.
// Es la misma logica de herramientas/conciliar_pagos.py (que sigue sirviendo como respaldo): cada
// registro PAGADO de la BD se busca en el reporte del banco y se clasifica como
//   OK        coincide con una transaccion APROBADA (estado C)
//   DECLINADA coincide con una transaccion DECLINADA (estado X): se guardo como pagada por error
//   SIN_TRX   no hay transaccion en el banco (ej. codigo 3D Secure incorrecto): se guardo como pagada por error
// y cada transaccion APROBADA que no tiene registro es un pago real que no quedo en la BD.
//
// Referencia de pago (customerRef1 / referencia_banorte): plantel(2) + conceptos(10, hasta 5 claves de 2
// digitos, "00" de relleno) + matricula(14) [+ sufijo de hora(4)].
//
// Las fechas se manejan como segundos "naive" (la hora local tal cual viene, sin zona horaria), porque la BD
// y el reporte del banco estan en la misma hora local.

const dos = (n) => String(n).padStart(2, "0");

// ---------------------------------------------------------------- fechas
export const segundos = (anio, mes, dia, h = 0, mi = 0, s = 0) => Date.UTC(anio, mes - 1, dia, h, mi, s) / 1000;
const comoFecha = (ts) => new Date(ts * 1000);
export const fechaIso = (ts) => { const f = comoFecha(ts); return `${f.getUTCFullYear()}-${dos(f.getUTCMonth() + 1)}-${dos(f.getUTCDate())}`; };
export const horaIso = (ts) => { const f = comoFecha(ts); return `${dos(f.getUTCHours())}:${dos(f.getUTCMinutes())}:${dos(f.getUTCSeconds())}`; };
export const fechaHoraIso = (ts) => `${fechaIso(ts)} ${horaIso(ts)}`;
export const fechaMx = (ts) => { const f = comoFecha(ts); return `${dos(f.getUTCDate())}/${dos(f.getUTCMonth() + 1)}/${f.getUTCFullYear()}`; };
export const fechaHoraMx = (ts) => `${fechaMx(ts)} ${dos(comoFecha(ts).getUTCHours())}:${dos(comoFecha(ts).getUTCMinutes())}`;

// "2026-10-14" + "11:19:43(.123)" -> segundos; null si no se entiende
export function segundosDeBd(fecha, hora) {
  const f = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(fecha || ""));
  const h = /^(\d{1,2}):(\d{2}):(\d{2})/.exec(String(hora || ""));
  if (!f || !h) return null;
  return segundos(+f[1], +f[2], +f[3], +h[1], +h[2], +h[3]);
}

// "14/10/2026" + "12:04:40" (reporte del banco) -> segundos; null si no se entiende
export function segundosDeBanorte(fecha, hora) {
  const f = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(String(fecha || "").trim());
  const h = /^(\d{1,2}):(\d{2}):(\d{2})/.exec(String(hora || "").trim());
  if (!f || !h) return null;
  return segundos(+f[3], +f[2], +f[1], +h[1], +h[2], +h[3]);
}

// ---------------------------------------------------------------- lectura del reporte de Banorte
// Banorte pone un espacio de ancho cero antes de los codigos de autorizacion (para conservar los ceros).
const INVISIBLES = /[​‌‍﻿ ]/g;
export const limpiar = (v) => String(v === undefined || v === null ? "" : v).replace(INVISIBLES, "").trim();

const sinAcentos = (s) => String(s).normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

// El CSV de Banorte a veces trae el texto en UTF-8 leido como Latin-1 ("mÃ³dulo"); se arregla.
export function repararTexto(texto) {
  const s = String(texto || "");
  if (!/[ÂÃ]/.test(s)) return s;
  try {
    return decodeURIComponent(escape(s));
  } catch (error) {
    return s;
  }
}

// Texto CSV (coma, comillas dobles opcionales) -> arreglo de filas
export function parsearCsv(texto) {
  const t = String(texto || "").replace(/^﻿/, "");
  const filas = [];
  let fila = [];
  let campo = "";
  let entreComillas = false;
  for (let i = 0; i < t.length; i++) {
    const c = t[i];
    if (entreComillas) {
      if (c === '"') {
        if (t[i + 1] === '"') { campo += '"'; i++; } else { entreComillas = false; }
      } else {
        campo += c;
      }
    } else if (c === '"') {
      entreComillas = true;
    } else if (c === ",") {
      fila.push(campo); campo = "";
    } else if (c === "\n" || c === "\r") {
      if (c === "\r" && t[i + 1] === "\n") i++;
      fila.push(campo); campo = "";
      if (fila.some((x) => x !== "")) filas.push(fila);
      fila = [];
    } else {
      campo += c;
    }
  }
  fila.push(campo);
  if (fila.some((x) => x !== "")) filas.push(fila);
  return filas;
}

// tabla = arreglo de filas (la primera es el encabezado) -> transacciones ordenadas por hora
export function transaccionesDeBanorte(tabla) {
  const t = (tabla || []).map((fila) => fila.map(limpiar));
  if (t.length < 2) throw new Error("El archivo de Banorte no trae transacciones.");
  const cab = t[0].map(sinAcentos);
  const idx = (prefijo) => {
    const i = cab.findIndex((n) => n.startsWith(prefijo));
    if (i < 0) throw new Error(`No encuentro la columna "${prefijo}" en el reporte de Banorte. Debe ser el reporte "Transacciones Payworks 2".`);
    return i;
  };
  const I = {
    fecha: idx("fecha de trans"), hora: idx("hora de trans"), monto: idx("monto"), estado: idx("estado"),
    ref1: idx("referencia 1"), desc: idx("descripci"), aut: idx("codigo de autorizacion"),
  };
  const salida = [];
  t.slice(1).forEach((r, n) => {
    if (!r[I.fecha]) return;
    const ts = segundosDeBanorte(r[I.fecha], r[I.hora]);
    if (ts === null) throw new Error(`No entiendo la fecha u hora de la fila ${n + 2} del reporte de Banorte (${r[I.fecha]} ${r[I.hora]}).`);
    salida.push({
      ts,
      monto: parseFloat(String(r[I.monto]).replace(/[$,]/g, "")),
      estado: r[I.estado].trim(),
      desc: repararTexto(r[I.desc]),
      ref1: r[I.ref1].trim(),
      aut: r[I.aut].trim(),
    });
  });
  salida.sort((a, b) => a.ts - b.ts);
  return salida;
}

// Filas de la BD (tal como las manda wMultipagosConciliacionPagos.php) -> registros
export function registrosDeBd(filas) {
  return (filas || []).map((r) => ({
    id: parseInt(r.id, 10),
    mat: String(r.codigo_alumno || "").trim(),
    monto: parseFloat(r.importe_total),
    ts: segundosDeBd(r.fecha_creacion, r.hora_creacion),
    ref: String(r.referencia_banorte || "").trim(),
    estatus: String(r.estatus || "").trim(),
    folio: String(r.folio_banco || "").trim(),
  })).filter((r) => r.ts !== null && !Number.isNaN(r.id));
}

// ---------------------------------------------------------------- utilidades de la referencia
export const matDeRef1 = (r1) => (r1.length >= 26 ? r1.substring(12, 26) : "");

export function clavesDeRef1(r1) {
  const cs = r1.substring(2, 12);
  const claves = [];
  for (let i = 0; i < 10; i += 2) {
    const c = cs.substring(i, i + 2);
    if (c !== "00") claves.push(c);
  }
  return claves;
}

// Generaciones 2011-2016: el plantel ocupa 3 posiciones (7 a 9) de la matricula; el resto, 2 (7 y 8)
export function plantelOficial(mat) {
  const anio = parseInt(mat.substring(0, 2), 10);
  return anio >= 11 && anio <= 16 ? String(parseInt(mat.substring(7, 10), 10)).padStart(2, "0") : mat.substring(7, 9);
}

// el id de plantel en la BD va corrido en 2 respecto al plantel oficial (igual que la app)
export const idPlantelBd = (mat) => String(parseInt(plantelOficial(mat), 10) + 2);

const REFERENCIA_VALIDA = /^\d{26}(\d{4})?$/;
const AUTORIZACION_VALIDA = /^[A-Za-z0-9]{1,12}$/;
const MATRICULA_VALIDA = /^\d{14}$/;

// ---------------------------------------------------------------- conciliacion
const minPor = (lista, clave) => {
  let mejor = null;
  let mejorValor = Infinity;
  lista.forEach((x) => {
    const v = clave(x);
    if (v < mejorValor) { mejor = x; mejorValor = v; }
  });
  return mejor;
};

// Marca cada registro con b (transaccion de Banorte), como y clase. Regresa las transacciones APROBADAS sin registro.
export function conciliar(bd, bk) {
  const usado = new Set();
  bd.forEach((d) => { d.b = null; d.como = undefined; });

  // 1) por referencia exacta (la BD guarda la referencia que viaja en customerRef1)
  const porRef = new Map();
  bk.forEach((b, i) => {
    if (!porRef.has(b.ref1)) porRef.set(b.ref1, []);
    porRef.get(b.ref1).push(i);
  });
  bd.forEach((d) => {
    if (!d.ref) return;
    for (const i of porRef.get(d.ref) || []) {
      if (!usado.has(i)) {
        d.b = bk[i]; d.como = "referencia"; usado.add(i);
        break;
      }
    }
  });

  const porHora = [...bd].sort((x, y) => x.ts - y.ts);

  // 2) por hora (+-20 s), importe y matricula
  porHora.forEach((d) => {
    if (d.b) return;
    let c = [];
    bk.forEach((b, i) => {
      if (!usado.has(i) && Math.abs(b.ts - d.ts) <= 20 && b.monto === d.monto) c.push([i, b]);
    });
    const conMatricula = c.filter(([, b]) => matDeRef1(b.ref1) === d.mat);
    c = conMatricula.length ? conMatricula : (d.ref ? [] : c);
    if (c.length) {
      const [i, b] = minPor(c, ([, x]) => Math.abs(x.ts - d.ts));
      d.b = b; d.como = "hora"; usado.add(i);
    }
  });

  // 3) segunda pasada: misma matricula e importe; la BD se guarda 0-300 s despues de la transaccion
  porHora.forEach((d) => {
    if (d.b) return;
    const c = [];
    bk.forEach((b, i) => {
      const dif = d.ts - b.ts;
      if (!usado.has(i) && b.monto === d.monto && matDeRef1(b.ref1) === d.mat && dif >= -5 && dif <= 300) c.push([i, b]);
    });
    if (c.length) {
      const [i, b] = minPor(c, ([, x]) => Math.abs(d.ts - x.ts));
      d.b = b; d.como = "hora tardia"; usado.add(i);
    }
  });

  bd.forEach((d) => { d.clase = d.b === null ? "SIN_TRX" : (d.b.estado === "C" ? "OK" : "DECLINADA"); });
  return bk.filter((b, i) => !usado.has(i) && b.estado === "C");
}

// ---------------------------------------------------------------- SQL
const idsSql = (ids) => [...ids].sort((a, b) => a - b).join(", ");
const dinero = (n) => n.toFixed(2);
const dineroMiles = (n) => n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const sqlTexto = (s) => String(s).replace(/'/g, "''");

// Genera los 6 scripts de correccion (mismo contenido que herramientas/conciliar_pagos.py).
// corte: segundos o null (la verificacion solo cuenta lo anterior al corte).
export function generarScripts({ bd, sinRegistro, fechaCorreccion, etiqueta, precios = {}, nombresPlanteles = {}, esperado = [0, 0], corte = null, bordes = [] }) {
  const falsos = bd.filter((d) => ["DECLINADA", "SIN_TRX"].includes(d.clase) && d.estatus === "PAGADO");
  const dec = falsos.filter((d) => d.clase === "DECLINADA");
  const sin = falsos.filter((d) => d.clase === "SIN_TRX");
  // Registros correctos a los que se les anota la autorizacion de Banorte en folio_banco: solo si la matricula y el
  // importe de la transaccion del banco coinciden exactamente con los del registro.
  const folios = bd.filter((d) => d.clase === "OK" && d.estatus === "PAGADO" && !d.folio && d.b.aut && AUTORIZACION_VALIDA.test(d.b.aut)
    && matDeRef1(d.b.ref1) === d.mat && d.b.monto === d.monto);
  const idsFolios = new Set(folios.map((d) => d.id));
  const omitidosFolio = bd.filter((d) => d.clase === "OK" && !d.folio && !idsFolios.has(d.id));
  const tablaRespI = `tblmultipagos_resp_${etiqueta}_intenciones`;
  const tablaRespD = `tblmultipagos_resp_${etiqueta}_detalles`;
  const tablaRespF = `tblmultipagos_resp_${etiqueta}_folios`;
  const tablaAltas = `tblmultipagos_resp_${etiqueta}_altas`;
  const idsFalsos = falsos.map((d) => d.id);
  const archivos = {};

  const respaldoFolios = folios.length
    ? `
-- Folios actuales de los ${folios.length} registros a los que el paso 4 les anota la autorizacion
CREATE TABLE ${tablaRespF} AS
SELECT id, folio_banco FROM tblmultipagos_intenciones WHERE id IN (${idsSql(idsFolios)});
`
    : "";
  archivos["1_respaldo.sql"] = `-- 1. RESPALDO (crea tablas nuevas; no modifica nada existente)
-- Guarda tal como estan hoy los ${idsFalsos.length} registros que se van a corregir y sus detalles.
CREATE TABLE ${tablaRespI} AS
SELECT * FROM tblmultipagos_intenciones WHERE id IN (${idsSql(idsFalsos)});

CREATE TABLE ${tablaRespD} AS
SELECT * FROM tblmultipagos_detalles WHERE id_intencion IN (${idsSql(idsFalsos)});
${respaldoFolios}
-- Tabla donde se anotan las altas del paso 3 (para poder revertirlas)
CREATE TABLE ${tablaAltas} (id_intencion integer PRIMARY KEY);

-- Verifica: debe decir ${idsFalsos.length}${folios.length ? ` y ${folios.length} en la segunda` : ""}
SELECT count(*) AS registros_respaldados FROM ${tablaRespI};
${folios.length ? `SELECT count(*) AS folios_respaldados FROM ${tablaRespF};` : ""}
`;

  const updateBloque = (ids, motivo, esperados) => {
    if (!ids.length) return "";
    return `DO $$
DECLARE n integer;
BEGIN
  UPDATE tblmultipagos_intenciones
     SET estatus = 'CANCELADO',
         importe_pagado = 0,
         comentarios = COALESCE(comentarios || ' | ', '') || '${motivo}'
   WHERE id IN (${idsSql(ids)})
     AND estatus = 'PAGADO'
     AND id_origen = 4;
  GET DIAGNOSTICS n = ROW_COUNT;
  IF n <> ${esperados} THEN
    RAISE EXCEPTION 'Se esperaban ${esperados} registros y se modificaron %. No se cambio nada.', n;
  END IF;
END $$;
`;
  };

  archivos["2_cancelar_registros_falsos.sql"] = `-- 2. CANCELAR LOS REGISTROS QUE QUEDARON COMO PAGADOS SIN SERLO
-- No se borra nada: el registro pasa a estatus CANCELADO (el historial del alumno y los totales por concepto
-- solo cuentan PAGADO y PARCIAL, asi que dejan de contarlo) y se anota el motivo en "comentarios".
-- Cada bloque DO es atomico: si el numero de filas no coincide, se cancela y no cambia nada.

-- 2a. Banorte DECLINO la transaccion (${dec.length} registros)
${updateBloque(dec.map((d) => d.id), `CORRECCION ${fechaCorreccion}: Banorte declino la transaccion; se habia guardado como pagada por error`, dec.length)}
-- 2b. No hubo transaccion en Banorte, p. ej. codigo 3D Secure incorrecto (${sin.length} registros)
${updateBloque(sin.map((d) => d.id), `CORRECCION ${fechaCorreccion}: no hubo transaccion en Banorte; se habia guardado como pagada por error`, sin.length)}
-- Verifica: deben salir ${idsFalsos.length} filas CANCELADO
SELECT id, codigo_alumno, importe_total, estatus FROM tblmultipagos_intenciones WHERE id IN (${idsSql(idsFalsos)}) ORDER BY id;
`;

  // ---- altas
  const bloques = [];
  const problemas = [];
  [...sinRegistro].sort((x, y) => x.ts - y.ts).forEach((b) => {
    const m = matDeRef1(b.ref1);
    const claves = clavesDeRef1(b.ref1);
    if (!m || !claves.length || b.monto === 1.0) return;
    if (!REFERENCIA_VALIDA.test(b.ref1) || !MATRICULA_VALIDA.test(m) || !AUTORIZACION_VALIDA.test(b.aut)) {
      problemas.push({ b, claves, precios: [], motivo: "la referencia, la matricula o la autorizacion no tienen el formato esperado" });
      return;
    }
    const preciosC = claves.map((c) => precios[c]);
    // un solo concepto: el precio es el importe; varios: se usan los precios del catalogo y se verifica la suma
    let lista;
    if (claves.length === 1) {
      lista = [[claves[0], b.monto]];
    } else if (preciosC.every((p) => p !== undefined && p !== null) && Math.abs(preciosC.reduce((a, p) => a + p, 0) - b.monto) < 0.005) {
      lista = claves.map((c, i) => [c, preciosC[i]]);
    } else {
      problemas.push({ b, claves, precios: preciosC, motivo: "los conceptos no suman el importe" });
      return;
    }
    const fecha = fechaIso(b.ts);
    const hora = horaIso(b.ts);
    const coment = `ALTA POR CORRECCION ${fechaCorreccion}: pago aprobado por Banorte (autorizacion ${b.aut}) que no se guardo por error`;
    const detalles = lista.map(([c, p]) =>
      `  INSERT INTO tblmultipagos_detalles (id_intencion, id_concepto, precio_unitario)\n`
      + `  SELECT nuevo, c.id, ${dinero(p)} FROM tblmultipagos_conceptos c WHERE c.clave = '${c}';\n`
      + `  GET DIAGNOSTICS n = ROW_COUNT;\n  IF n <> 1 THEN RAISE EXCEPTION 'La clave ${c} debe existir una sola vez en conceptos (hay %)', n; END IF;`).join("\n");
    const p = plantelOficial(m);
    bloques.push(`-- ${fecha} ${hora} | matricula ${m} | plantel ${p} ${nombresPlanteles[p] || ""} | $${dinero(b.monto)} | autorizacion ${b.aut} | conceptos ${lista.map(([c]) => c).join(", ")}
DO $$
DECLARE nuevo integer; n integer;
BEGIN
  IF EXISTS (SELECT 1 FROM tblmultipagos_intenciones WHERE referencia_banorte = '${b.ref1}') THEN
    RAISE EXCEPTION 'La referencia ${b.ref1} ya existe en la BD; no se duplica.';
  END IF;
  INSERT INTO tblmultipagos_intenciones
    (codigo_alumno, referencia_banorte, importe_total, fecha_creacion, hora_creacion, estatus, id_plantel, origen,
     folio_banco, id_banco, importe_pagado, comentarios, fecha_validacion, hora_validacion, id_origen)
  VALUES
    ('${m}', '${b.ref1}', ${dinero(b.monto)}, '${fecha}', '${hora}', 'PAGADO', '${idPlantelBd(m)}', 'AUTOSERVICIO',
     '${b.aut}', 1, ${dinero(b.monto)}, '${sqlTexto(coment)}', '${fecha}', '${hora}', 4)
  RETURNING id INTO nuevo;
${detalles}
  IF (SELECT COALESCE(SUM(precio_unitario), 0) FROM tblmultipagos_detalles WHERE id_intencion = nuevo) <> ${dinero(b.monto)} THEN
    RAISE EXCEPTION 'Los detalles no suman ${dinero(b.monto)}; no se cambio nada.';
  END IF;
  INSERT INTO ${tablaAltas} (id_intencion) VALUES (nuevo);
END $$;
`);
  });
  archivos["3_alta_pagos_aprobados_sin_registro.sql"] =
    `-- 3. DAR DE ALTA LOS PAGOS APROBADOS POR BANORTE QUE NO QUEDARON REGISTRADOS (${bloques.length} pagos)\n`
    + "-- Se crean con los mismos datos que habria guardado la app (origen portal en linea, banco Banorte, PAGADO) mas la\n"
    + "-- autorizacion de Banorte en folio_banco y una nota en comentarios. Cada bloque DO es atomico y no duplica referencias.\n"
    + "-- Los conceptos salen de las claves que lleva la propia referencia de pago.\n\n" + bloques.join("\n")
    + `\n-- Verifica: deben salir ${bloques.length} filas\nSELECT i.id, i.codigo_alumno, i.importe_total, i.estatus, i.folio_banco FROM tblmultipagos_intenciones i WHERE i.id IN (SELECT id_intencion FROM ${tablaAltas}) ORDER BY i.id;\n`;

  if (folios.length) {
    const valores = [...folios].sort((x, y) => x.id - y.id).map((d) => `(${d.id}, '${d.b.aut}', '${sqlTexto(d.mat)}')`).join(",\n    ");
    archivos["4_rellenar_folio_banco.sql"] = `-- 4. ANOTAR LA AUTORIZACION DE BANORTE EN LOS PAGOS CORRECTOS (${folios.length} registros)
-- La app no guardaba el numero de autorizacion de los pagos en linea. Aqui se anota en folio_banco (el mismo campo que ya
-- usan los demas pagos) para poder cotejar cada pago contra el voucher o el reporte del banco. Solo se llenan los
-- registros PAGADO que tienen folio_banco vacio y cuya matricula coincide con la de la transaccion aprobada de Banorte.
-- Es atomico: si el numero de filas no coincide, no cambia nada.
DO $$
DECLARE n integer;
BEGIN
  UPDATE tblmultipagos_intenciones i
     SET folio_banco = v.aut
    FROM (VALUES
    ${valores}
    ) AS v(id, aut, mat)
   WHERE i.id = v.id
     AND i.codigo_alumno = v.mat
     AND i.id_origen = 4
     AND i.estatus = 'PAGADO'
     AND COALESCE(i.folio_banco, '') = '';
  GET DIAGNOSTICS n = ROW_COUNT;
  IF n <> ${folios.length} THEN
    RAISE EXCEPTION 'Se esperaban ${folios.length} registros y se modificaron %. No se cambio nada.', n;
  END IF;
END $$;

-- Verifica: deben salir ${folios.length} filas con su autorizacion
SELECT id, codigo_alumno, importe_total, folio_banco FROM tblmultipagos_intenciones WHERE id IN (${idsSql(idsFolios)}) ORDER BY id;
`;
  }

  const revierteFolios = folios.length
    ? `  -- Regresa folio_banco a como estaba antes del paso 4
  UPDATE tblmultipagos_intenciones i
     SET folio_banco = r.folio_banco
    FROM ${tablaRespF} r
   WHERE i.id = r.id;
`
    : "";
  archivos["6_reversa.sql"] = `-- 6. REVERSA (solo si algo salio mal): deja todo como estaba antes de los pasos 2, 3 y 4
DO $$
BEGIN
  -- Regresa los registros cancelados a como estaban (estatus, importe pagado y comentarios)
  UPDATE tblmultipagos_intenciones i
     SET estatus = r.estatus, importe_pagado = r.importe_pagado, comentarios = r.comentarios
    FROM ${tablaRespI} r
   WHERE i.id = r.id;
${revierteFolios}  -- Borra las altas del paso 3 (sus detalles se borran solos)
  DELETE FROM tblmultipagos_intenciones WHERE id IN (SELECT id_intencion FROM ${tablaAltas});
END $$;
`;

  const tiempos = (bd.length ? bd : sinRegistro).map((d) => d.ts);
  const fIni = fechaIso(Math.min(...tiempos));
  const fFin = fechaIso(Math.max(...tiempos));
  const hastaCorte = corte !== null ? `\n   AND (fecha_creacion::text || ' ' || hora_creacion::text)::timestamp <= '${fechaIso(corte)} ${horaIso(corte).substring(0, 5)}'` : "";
  archivos["5_verificacion.sql"] = `-- 5. VERIFICACION (solo lectura). Pagos en linea del periodo segun la BD despues de la correccion
-- (hasta el corte${corte !== null ? ` ${fechaHoraMx(corte)}` : ""}; lo posterior no se reviso y no entra en esta cuenta).

-- 5a. Debe salir PAGADO con ${esperado[0]} registros por $${dineroMiles(esperado[1])} (= aprobadas por Banorte sin pruebas de $1)
${bordes.length ? `--     mas ${bordes.length} registro(s) de los bordes del periodo que no se juzgaron (ids ${idsSql(bordes.map((d) => d.id))}),
` : ""}--     y CANCELADO con ${falsos.length} registros.
SELECT estatus, count(*) AS registros, SUM(importe_total) AS importe
  FROM tblmultipagos_intenciones
 WHERE id_origen = 4 AND fecha_creacion BETWEEN '${fIni}' AND '${fFin}'${hastaCorte}
 GROUP BY estatus ORDER BY estatus;

-- 5b. Pagos PAGADO del periodo que siguen sin autorizacion (folio_banco): se espera ninguno
--     (solo apareceria alguno si no se pudo cotejar con exactitud contra Banorte).
SELECT id, codigo_alumno, importe_total, fecha_creacion, hora_creacion
  FROM tblmultipagos_intenciones
 WHERE id_origen = 4 AND estatus = 'PAGADO' AND COALESCE(folio_banco, '') = ''
   AND fecha_creacion BETWEEN '${fIni}' AND '${fFin}'${hastaCorte}
 ORDER BY id;
`;
  return { archivos, falsos, dec, sin, bloques, problemas, folios, omitidosFolio };
}

// ---------------------------------------------------------------- casos por alumno / plantel
export const ENCABEZADOS_CASOS = [
  "Plantel", "Nombre del plantel", "Matricula", "Situacion", "Que se hace", "Pagos APROBADOS por Banorte",
  "Registros que se cancelan (decian PAGADO)", "Pago aprobado que se da de alta",
];

// Un renglon por alumno con anomalias (los que no tienen ninguna no aparecen).
export function casosPorAlumno({ bd, bk, sinRegistro, nombresPlanteles = {} }) {
  const casos = new Map();
  const de = (m) => {
    if (!casos.has(m)) casos.set(m, { aprob: [], sinreg: [], falsos: [], ok: [] });
    return casos.get(m);
  };
  bk.forEach((b) => { if (b.estado === "C" && b.monto !== 1.0) de(matDeRef1(b.ref1)).aprob.push(b); });
  sinRegistro.forEach((b) => { if (b.monto !== 1.0) de(matDeRef1(b.ref1)).sinreg.push(b); });
  bd.forEach((d) => {
    if (d.clase === "OK") {
      de(d.mat).ok.push(d);
    } else if (d.clase !== "BORDE") {
      const motivo = d.clase === "SIN_TRX" ? "sin transaccion en Banorte" : `declinada por Banorte: ${d.b.desc}`;
      de(d.mat).falsos.push([d, motivo]);
    }
  });
  const dinero0 = (n) => String(Math.round(n));
  const filas = [];
  casos.forEach((c, m) => {
    if (!c.falsos.length && !c.sinreg.length) return;
    const p = plantelOficial(m);
    let sit;
    let acc;
    if (!c.aprob.length) {
      sit = "1-URGENTE: NO HA PAGADO";
      acc = "El sistema lo mostraba como pagado pero Banorte nunca aprobo un cobro en linea. Revisar si pago por otro medio (ventanilla o TPV); si no, no entregar servicio ni comprobante: el alumno debe pagar.";
    } else if (c.sinreg.length && !c.falsos.length) {
      sit = "2-SI PAGO, no tenia registro";
      acc = "Banorte aprobo el pago; se da de alta en el sistema.";
    } else if (c.sinreg.length) {
      sit = "3-SI PAGO, el registro quedo en otro intento";
      acc = "Banorte aprobo el pago (autorizacion indicada); se corrigen los registros de intentos no cobrados y se da de alta el aprobado.";
    } else {
      sit = "4-SI PAGO, habia registros de mas";
      acc = "El pago aprobado ya estaba registrado; se cancelan los registros de intentos rechazados.";
    }
    const unaTrx = (b) => `${fechaHoraMx(b.ts)} $${dinero0(b.monto)} aut ${b.aut}`;
    const ap = c.aprob.map(unaTrx).join("; ") || "ninguno";
    const mal = [...c.falsos].sort((x, y) => x[0].ts - y[0].ts).map(([d, mo]) => `#${d.id} ${fechaHoraMx(d.ts)} $${dinero0(d.monto)} (${mo})`).join("; ") || "ninguno";
    const sr = c.sinreg.map(unaTrx).join("; ") || "ninguno";
    filas.push([p, nombresPlanteles[p] || "", m, sit, acc, ap, mal, sr]);
  });
  filas.sort((x, y) => (x[0] < y[0] ? -1 : x[0] > y[0] ? 1 : x[3] < y[3] ? -1 : x[3] > y[3] ? 1 : x[2] < y[2] ? -1 : x[2] > y[2] ? 1 : 0));
  return filas;
}

// ---------------------------------------------------------------- todo junto
const MARGEN_RECIENTE = 10 * 60; // un pago aprobado hace menos de 10 min puede estar guardandose todavia
const MARGEN_BORDE = 10 * 60; // un registro de los primeros 10 min del periodo sin transaccion puede ser de una transaccion anterior al reporte
const MARGEN_GUARDADO = 10 * 60; // la BD guarda el pago segundos (en versiones viejas, minutos) despues de la transaccion

// registros: filas de la BD (PAGADO y otros) del periodo; transacciones: del reporte de Banorte.
// horizonte: segundos; las transacciones posteriores no se analizan. Los registros de la BD se analizan hasta
// horizonte + margenGuardado (la BD guarda el pago un poco despues de la transaccion); los que no tienen transaccion
// y quedan despues del horizonte no se pueden juzgar (el reporte aun no llegaba) y quedan como "BORDE".
// ahora: segundos (hora de la consulta). inicio: segundos del comienzo del periodo; los registros de sus primeros
// minutos que no tienen transaccion tampoco se juzgan (la transaccion pudo ser anterior al reporte).
export function analizarConciliacion({ registros, transacciones, horizonte = null, ahora = null, inicio = null, margenGuardado = MARGEN_GUARDADO, precios = {}, nombresPlanteles = {}, fechaCorreccion, etiqueta }) {
  const todos = registros;
  const pagados = todos.filter((r) => r.estatus === "PAGADO");
  const otros = todos.filter((r) => r.estatus !== "PAGADO");
  const hastaBd = horizonte === null ? null : horizonte + margenGuardado;
  const bd = pagados.filter((r) => hastaBd === null || r.ts <= hastaBd);
  const posteriores = pagados.filter((r) => hastaBd !== null && r.ts > hastaBd);
  const bk = transacciones.filter((b) => horizonte === null || b.ts <= horizonte);
  const sinRegistroTodos = bd.length || bk.length ? conciliar(bd, bk) : [];
  bd.forEach((d) => {
    const alInicio = inicio !== null && d.ts < inicio + MARGEN_BORDE;
    const alFinal = horizonte !== null && d.ts > horizonte;
    if (d.clase === "SIN_TRX" && (alInicio || alFinal)) d.clase = "BORDE";
  });
  const borde = bd.filter((d) => d.clase === "BORDE");
  const recientes = ahora === null ? [] : sinRegistroTodos.filter((b) => ahora - b.ts < MARGEN_RECIENTE);
  const sinRegistro = ahora === null ? sinRegistroTodos : sinRegistroTodos.filter((b) => ahora - b.ts >= MARGEN_RECIENTE);

  const aprobadas = bk.filter((b) => b.estado === "C");
  const esperados = aprobadas.filter((b) => b.monto !== 1.0 && !recientes.includes(b));
  // los registros de los bordes no se corrigen: siguen PAGADO y cuentan en lo que debe quedar
  const esperado = [esperados.length + borde.length, esperados.reduce((a, b) => a + b.monto, 0) + borde.reduce((a, d) => a + d.monto, 0)];
  const ok = bd.filter((d) => d.clase === "OK");
  const hayDatos = bd.length > 0 || sinRegistro.length > 0;
  const scripts = hayDatos
    ? generarScripts({ bd, sinRegistro, fechaCorreccion: fechaCorreccion, etiqueta, precios, nombresPlanteles, esperado, corte: hastaBd, bordes: borde })
    : { archivos: {}, falsos: [], dec: [], sin: [], bloques: [], problemas: [], folios: [], omitidosFolio: [] };
  const casos = casosPorAlumno({ bd, bk, sinRegistro, nombresPlanteles });
  const pruebasUno = sinRegistro.filter((b) => b.monto === 1.0);

  return {
    bd, bk, ok, otros, posteriores, borde, recientes, sinRegistro, pruebasUno, aprobadas, esperado, casos, ...scripts,
    resumen: {
      registros: bd.length,
      transacciones: bk.length,
      aprobadas: aprobadas.length,
      declinadas: bk.length - aprobadas.length,
      ok: ok.length,
      declinadasGuardadas: scripts.dec.length,
      importeDeclinadas: scripts.dec.reduce((a, d) => a + d.monto, 0),
      sinTransaccion: scripts.sin.length,
      importeSinTransaccion: scripts.sin.reduce((a, d) => a + d.monto, 0),
      aprobadasSinRegistro: sinRegistro.length,
      importeSinRegistro: sinRegistro.reduce((a, b) => a + b.monto, 0),
      altas: scripts.bloques.length,
      folios: scripts.folios.length,
      esperadoRegistros: esperado[0],
      esperadoImporte: esperado[1],
      alumnosConAnomalias: casos.length,
    },
  };
}

// Etiqueta de las tablas de respaldo: AAAAMMDD de la fecha de correccion (segundos)
export const etiquetaDeFecha = (ts) => fechaIso(ts).replace(/-/g, "");

// Igual, con la hora (AAAAMMDD_HHMM): asi dos correcciones el mismo dia no chocan en el nombre de sus tablas de respaldo
export const etiquetaDeFechaHora = (ts) => `${etiquetaDeFecha(ts)}_${horaIso(ts).substring(0, 5).replace(":", "")}`;

// Todos los scripts en un solo texto, cada uno con su titulo, en el orden en que se corren
export function scriptsEnUnArchivo(archivos) {
  return Object.keys(archivos).sort().map((nombre) => `-- ==================== ${nombre} ====================\n${archivos[nombre]}`).join("\n\n");
}
